import dayjs, { ConfigType, Dayjs } from "dayjs";

/**
 * 过滤条件 DTO
 */
export interface GuoLvTiaoJianDTO {
  /**
   * 字段名，单字段传字符串；CONCAT_IN 时可传字符串 "field1,field2" 或数组 ["field1", "field2"]
   * @example "advertiser_id"
   */
  field: string;
  /**
   * 操作符
   * @example "EQ"
   */
  operator:
      | "EQ"
      | "NE"
      | "GT"
      | "GE"
      | "LT"
      | "LE"
      | "IN"
      | "NOT_IN"
      | "LIKE"
      | "NOT_LIKE"
      | "BETWEEN"
      | "NOT_BETWEEN"
      | "SPECIAL";
  /**
   * 特殊查询类型
   * @example "ADVERTISER_KEYWORD"
   */
  specialQueryType?: "ADVERTISER_KEYWORD" | "CONCAT_IN";
  /**
   * 值
   */
  value?: any;
  /**
   * 第二个值（用于 BETWEEN 等）
   */
  value2?: any;
}

/**
 * 动态报表查询 DTO
 */
export interface DongTaiBaoBiaoChaXunDTO {
  /**
   * 查询维度列表（可选，为空时只聚合数据）
   * @example ["advertiser_id","campaign_id"]
   */
  dimensions?: string[];
  /**
   * 结束时间
   * @example "2023-01-02 00:00:00"
   */
  endTime: string;
  /** 过滤条件列表 */
  filters?: GuoLvTiaoJianDTO[];
  /**
   * 查询指标列表
   * @example ["imp_cnt","click_cnt","spent"]
   */
  metrics: string[];
  /**
   * 页码
   * @format int32
   */
  pageNum?: number;
  /**
   * 每页数量
   * @format int32
   */
  pageSize?: number;
  /**
   * 排序字段
   * @example "spent"
   */
  sortField?: string;
  /**
   * 排序方式
   * @example "DESC"
   */
  sortOrder?: "ASC" | "DESC";
  /**
   * 开始时间
   * @example "2023-01-01 00:00:00"
   */
  startTime: string;
  /**
   * 表代码
   * @example "AD_DATA_GRANULARITY_30M"
   */
  tableCode: string;
  /**
   * 时区
   * @example "UTC"
   */
  timezone?: string;
}

/**
 * 维度键值映射
 */
export const DIMENSION_KEYS = {
  date: "event_time_to_date",
  hour: "event_time_to_hour",
  week: "event_time_to_dayofweek",
  month: "event_time_to_month",
  media: "media_id",
  mediaName: "media_name",
  publisher: "publisher_id",
  publisherName: 'publisher_name',
  app: "app_id",
  adTypeName: "ad_type_name",
  adTypeId: "ad_type",
  country: "country",
  // 广告主相关维度
  advertiserId: "advertiser_id",
  advertiserName: "advertiser_name",
  advertiserKeyword: "advertiser_keyword",
  campaignId: "campaign_id",
  campaignName: "campaign_name",
  adGroupId: "adgroup_id",
  adGroupName: "ad_group_name",
  adId: "ad_id",
  adName: "ad_name",
  os: "os",
  deviceType: "device_type",
  region: "region",
  business: "user_id",
  businessName: "user_name",
  // 产品相关
  productId: "product_id",
  productName: "product_name",
} as const;

/**
 * 指标键值映射
 */
export const METRIC_KEYS = {
  bidCount: "bid_cnt",
  winCount: "win_cnt",
  impressionCount: "imp_cnt",
  clickCount: "click_cnt",
  conversionCount: "target_cnt",
  targetConversionCount: 'target_conv_cnt',
  revenue: "spent",
  cost: "price",
  spent: "spent",
  grossProfit: "gross_profit",
  grossProfitMargin: "gross_profit_margin",
  ecpm: "ecpm",
  ecpc: "cpc",
  ecpa: "cpa",
  ctr: "ctr",
  cvr: "cvr",
  requestCnt: "request_cnt",
} as const;

/**
 * 表代码常量
 */
export const TABLE_CODES = {
  AD_DATA_GRANULARITY_30M: "AD_DATA_GRANULARITY_30M",
  MEDIA_AD_FULL_AGG_30M: "MEDIA_AD_FULL_AGG_30M",
  MEDIA_APP: "MEDIA_APP",
  MEDIA_SITE: "MEDIA_SITE"
} as const;

/**
 * 动态报表查询参数
 */
export interface DynamicReportParams {
  /** 表代码 */
  tableCode: string;
  /** 维度列表 */
  dimensions: string[];
  /** 指标列表 */
  metrics: string[];
  /** 开始时间 */
  startTime: string | Dayjs;
  /** 结束时间 */
  endTime: string | Dayjs;
  /** 过滤条件 */
  filters?: GuoLvTiaoJianDTO[];
  /** 排序字段 */
  sortField?: string;
  /** 排序方式 */
  sortOrder?: "ASC" | "DESC";
  /** 页码 */
  pageNum?: number;
  /** 每页数量 */
  pageSize?: number;
  /** 时区 */
  timezone?: string;
}

/**
 * 格式化日期时间
 * @param time 输入的时间 (string, number, Date, 或 Dayjs)
 * @param isEnd 是否是结束时间（若是则补全为 23:59:59）
 */
const formatDateTime = (time: ConfigType, isEnd: boolean): string => {
  if (!time) return '';

  const d = dayjs(time);
  if (!d.isValid()) return String(time);

  const timeStr = typeof time === 'string' ? time : '';
  const hasTime = timeStr.includes(':');

  if (hasTime) {
    return d.format('YYYY-MM-DD HH:mm:ss');
  }

  return isEnd
      ? d.endOf('day').format('YYYY-MM-DD HH:mm:ss')
      : d.startOf('day').format('YYYY-MM-DD HH:mm:ss');
};

export const buildDynamicReportQuery = (
    params: DynamicReportParams
): DongTaiBaoBiaoChaXunDTO => {
  const {
    startTime,
    endTime,
    filters = [],
    sortOrder = "DESC",
    pageNum = 1,
    pageSize = 20,
    timezone = "UTC",
    ...rest
  } = params;

  return {
    ...rest,
    startTime: formatDateTime(startTime, false),
    endTime: formatDateTime(endTime, true),
    filters,
    sortOrder,
    pageNum,
    pageSize,
    timezone,
  };
};

/**
 * 创建过滤条件
 */
export const createFilter = (
    field: string,
    operator: GuoLvTiaoJianDTO["operator"],
    value: any,
    value2?: any,
    specialQueryType?: string
): GuoLvTiaoJianDTO => {
  const filter: GuoLvTiaoJianDTO = {
    field,
    operator,
    value,
  };

  if (value2 !== undefined) {
    filter.value2 = value2;
  }

  if (specialQueryType) {
    filter.specialQueryType = specialQueryType as any;
  }

  return filter;
};

/**
 * 从返回数据中提取值
 */
export const extractValueFromRow = (
    row: any,
    field: string,
    defaultValue: any = ""
): any => {
  // 优先从 dimensions 中获取
  if (row.dimensions && row.dimensions[field] !== undefined) {
    return row.dimensions[field];
  }

  // 然后从 metrics 中获取
  if (row.metrics && row.metrics[field] !== undefined) {
    return row.metrics[field];
  }

  // 最后从 rawData 中获取
  if (row.rawData && row.rawData[field] !== undefined) {
    return row.rawData[field];
  }

  return defaultValue;
};

/**
 * 格式化动态报表返回数据为前端使用的格式
 */
export const formatDynamicReportData = <T = any>(
    data: any,
    fieldMapping: Record<string, string>
): T => {
  const result: any = {};

  Object.entries(fieldMapping).forEach(([frontendKey, backendKey]) => {
    result[frontendKey] = extractValueFromRow(data, backendKey);
  });

  return result as T;
};

/**
 * 通用过滤器构建配置
 */
export interface FilterConfig {
  field: string;
  operator: GuoLvTiaoJianDTO["operator"];
  specialQueryType?: string;
}

/**
 * 通用过滤器构建器
 */
export const buildCommonFilters = (
    values: Record<string, any>,
    filterConfigs: Record<string, FilterConfig>
): GuoLvTiaoJianDTO[] => {
  return Object.entries(filterConfigs)
  .filter(([key]) => {
    const value = values[key];
    return value != null && value !== '' && (!Array.isArray(value) || value.length > 0);
  })
  .map(([key, config]) =>
      createFilter(config.field, config.operator, values[key], undefined, config.specialQueryType)
  );
};

/**
 * 批量格式化动态报表数据
 */
export const formatDynamicReportList = <T = any>(
    dataList: any[],
    fieldMapping: Record<string, string>
): T[] => {
  return dataList.map((item) => formatDynamicReportData<T>(item, fieldMapping));
};
